import { createContext, useCallback, useContext, useReducer } from "react";

const initialState = {
  mode: "IDLE",
  inputValue: "",
  submittedTerm: "",
};

function reducer(state, action) {
  switch (action.type) {
    case "SET_INPUT": {
      return { ...state, inputValue: action.value };
    }
    case "FOCUS": {
      if (state.mode === "RESULTS_LOADING") {
        return state;
      }
      if (state.mode === "RESULTS") {
        return { ...state, mode: "FOCUSING" };
      }
      return { ...state, mode: "FOCUSING" };
    }
    case "SUBMIT": {
      const term = action.term.trim();
      if (!term) {
        return { ...initialState };
      }
      return {
        mode: "RESULTS_LOADING",
        inputValue: term,
        submittedTerm: term,
      };
    }
    case "RESULTS_READY": {
      if (!state.submittedTerm) {
        return state;
      }
      return { ...state, mode: "RESULTS" };
    }
    case "RESET": {
      return { ...initialState };
    }
    case "BLUR": {
      const hasTerm = Boolean(state.inputValue.trim() || state.submittedTerm);
      if (hasTerm) {
        if (state.submittedTerm && state.mode === "FOCUSING") {
          return { ...state, mode: "RESULTS" };
        }
        return state;
      }
      return { ...initialState };
    }
    default:
      return state;
  }
}

const AppModeContext = createContext(null);

export function AppModeProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const setInput = useCallback(
    (value) => dispatch({ type: "SET_INPUT", value }),
    [],
  );

  const focus = useCallback(() => dispatch({ type: "FOCUS" }), []);

  const submit = useCallback(
    (term) => dispatch({ type: "SUBMIT", term }),
    [],
  );

  const reset = useCallback(() => dispatch({ type: "RESET" }), []);

  const blur = useCallback(() => dispatch({ type: "BLUR" }), []);

  const markResultsReady = useCallback(
    () => dispatch({ type: "RESULTS_READY" }),
    [],
  );

  const value = {
    mode: state.mode,
    inputValue: state.inputValue,
    submittedTerm: state.submittedTerm,
    setInput,
    focus,
    submit,
    reset,
    blur,
    markResultsReady,
  };

  return (
    <AppModeContext.Provider value={value}>{children}</AppModeContext.Provider>
  );
}

export function useAppMode() {
  const ctx = useContext(AppModeContext);
  if (!ctx) {
    throw new Error("useAppMode must be used within an AppModeProvider");
  }
  return ctx;
}
